import React from 'react';
import { Mail, Star, Send, FileText, Trash2, AlertCircle, Archive, Inbox } from 'lucide-react';

interface LoadingStateProps {
  folderName: string;
  type?: 'default' | 'skeleton';
}

const LoadingState: React.FC<LoadingStateProps> = ({ folderName, type = 'default' }) => {
  if (type === 'skeleton') {
    return (
      <div className="animate-pulse" style={{ width: '100%', padding: '0 24px' }}>
        {/* Generate 10 skeleton items */}
        {Array.from({ length: 10 }).map((_, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px 0',
              borderBottom: '1px solid rgb(204, 205, 209)',
              width: '100%'
            }}
          >
            {/* Checkbox skeleton */}
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '4px',
              backgroundColor: 'rgb(204, 205, 209)'
            }} />

            {/* Avatar skeleton */}
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: 'rgb(204, 205, 209)',
              flexShrink: 0
            }} />

            {/* Content skeleton */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Name and time */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <div style={{
                  width: '150px',
                  height: '14px',
                  backgroundColor: 'rgb(204, 205, 209)',
                  borderRadius: '4px'
                }} />
                <div style={{
                  width: '80px',
                  height: '12px',
                  backgroundColor: 'rgb(204, 205, 209)',
                  borderRadius: '4px'
                }} />
              </div>

              {/* Subject */}
              <div style={{
                width: '70%',
                height: '16px',
                backgroundColor: 'rgb(204, 205, 209)',
                borderRadius: '4px',
                marginBottom: '8px'
              }} />

              {/* Preview text */}
              <div style={{
                width: '90%',
                height: '14px',
                backgroundColor: 'rgb(204, 205, 209)',
                borderRadius: '4px'
              }} />
            </div>

            {/* Actions skeleton */}
            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center'
            }}>
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    backgroundColor: 'rgb(204, 205, 209)'
                  }}
                />
              ))}
            </div>
          </div>
        ))}

        <style dangerouslySetInnerHTML={{
          __html: `
            @keyframes pulse {
              0%, 100% {
                opacity: 1;
              }
              50% {
                opacity: 0.5;
              }
            }
            
            .animate-pulse {
              animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
            }
          `
        }} />
      </div>
    );
  }

  const getFolderIcon = () => {
    const iconSize = 48;
    const iconColor = '#8D00E4';
    
    switch (folderName.toUpperCase()) {
      case 'INBOX':
        return <Inbox size={iconSize} color={iconColor} />;
      case 'STARRED':
        return <Star size={iconSize} color={iconColor} />;
      case 'SENT':
        return <Send size={iconSize} color={iconColor} />;
      case 'DRAFTS':
        return <FileText size={iconSize} color={iconColor} />;
      case 'TRASH':
        return <Trash2 size={iconSize} color={iconColor} />;
      case 'SPAM':
      case 'JUNK':
        return <AlertCircle size={iconSize} color={iconColor} />;
      case 'ARCHIVE':
        return <Archive size={iconSize} color={iconColor} />;
      default:
        return <Mail size={iconSize} color={iconColor} />;
    }
  };

  const getLoadingMessage = () => {
    switch (folderName.toUpperCase()) {
      case 'INBOX':
        return 'Loading your inbox...';
      case 'STARRED':
        return 'Loading starred messages...';
      case 'SENT':
        return 'Loading sent messages...';
      case 'DRAFTS':
        return 'Loading drafts...';
      case 'TRASH':
        return 'Loading deleted messages...';
      case 'SPAM':
      case 'JUNK':
        return 'Loading spam folder...';
      case 'ARCHIVE':
        return 'Loading archived messages...';
      default:
        return 'Loading messages...';
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      minHeight: '90vh',
      width: '100%',
      margin: '20px',
      padding: '40px 20px',
      background: '#ffffff80',
      borderRadius: '12px',
      border: '1px solid rgb(210, 213, 220)'
    }}>
      {/* Animated Icon */}
      <div style={{
        position: 'relative',
        marginBottom: '24px'
      }}>
        <div style={{
          animation: 'pulse 2s infinite',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {getFolderIcon()}
        </div>
        
        {/* Loading dots */}
        <div style={{
          position: 'absolute',
          bottom: '-8px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '4px'
        }}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: '#8D00E4',
                animation: `bounce 1.4s ease-in-out infinite both`,
                animationDelay: `${i * 0.16}s`
              }}
            />
          ))}
        </div>
      </div>

      {/* Loading Text */}
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        color: '#374151',
        margin: '0 0 8px 0',
        textAlign: 'center'
      }}>
        {getLoadingMessage()}
      </h3>
      
      <p style={{
        fontSize: '14px',
        color: '#6b7280',
        margin: '0',
        textAlign: 'center',
        maxWidth: '300px'
      }}>
        Please wait while we fetch your messages
      </p>

      {/* Progress Bar */}
      <div style={{
        width: '200px',
        height: '4px',
        backgroundColor: '#e5e7eb',
        borderRadius: '2px',
        marginTop: '24px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: '30%',
          height: '100%',
          backgroundColor: '#8D00E4',
          borderRadius: '2px',
          animation: 'shimmer 2s infinite'
        }} />
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.7;
              transform: scale(1.05);
            }
          }
          
          @keyframes bounce {
            0%, 80%, 100% {
              transform: scale(0);
            }
            40% {
              transform: scale(1);
            }
          }
          
          @keyframes shimmer {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(400%);
            }
          }
        `
      }} />
    </div>
  );
};

export default LoadingState; 